import { ChevronLeft, ChevronRight, Maximize2, Rows3, Square, ZoomIn, ZoomOut } from "lucide-react"
import type * as React from "react"

import { Button, ButtonGroup } from "@/components/ui/button"
import { IconButton } from "@/components/ui/icon-button"

import { FINDING_TONE, OUTCOME_TONE } from "../eval-runs-data"

import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./use-zoom-pan"

export type PageMode = "single" | "continuous"

export function ViewerToolbar(props: {
  page: number
  pageCount: number
  onPage: (page: number) => void
  mode: PageMode
  onMode: (mode: PageMode) => void
  zoom: number
  onZoom: (zoom: number) => void
  /** The box legend; off for a document with nothing drawn on it. */
  legend?: boolean
  /** A quiet line beside the pager. */
  note?: React.ReactNode
}) {
  const { page, pageCount, zoom } = props
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-surface px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Pages">
        <IconButton
          label="Previous page"
          icon={<ChevronLeft />}
          disabled={page <= 0}
          onClick={() => {
            props.onPage(page - 1)
          }}
        />
        <span className="min-w-20 text-center text-body-sm tabular-nums text-text-2">
          Page {page + 1} of {pageCount}
        </span>
        <IconButton
          label="Next page"
          icon={<ChevronRight />}
          disabled={page + 1 >= pageCount}
          onClick={() => {
            props.onPage(page + 1)
          }}
        />
        {props.note}
      </div>

      <span aria-hidden className="hidden h-5 w-px bg-border sm:block" />

      <ButtonGroup label="Page view">
        <Button
          size="sm"
          variant={props.mode === "single" ? "default" : "ghost"}
          aria-pressed={props.mode === "single"}
          leadingIcon={<Square />}
          onClick={() => {
            props.onMode("single")
          }}
        >
          Single page
        </Button>
        <Button
          size="sm"
          variant={props.mode === "continuous" ? "default" : "ghost"}
          aria-pressed={props.mode === "continuous"}
          leadingIcon={<Rows3 />}
          onClick={() => {
            props.onMode("continuous")
          }}
        >
          Scroll
        </Button>
      </ButtonGroup>

      <span aria-hidden className="hidden h-5 w-px bg-border sm:block" />

      <div className="flex items-center gap-1" role="group" aria-label="Zoom">
        <IconButton
          label="Zoom out"
          icon={<ZoomOut />}
          disabled={zoom <= ZOOM_MIN}
          onClick={() => {
            props.onZoom(zoom / ZOOM_STEP)
          }}
        />
        <span className="w-12 text-center text-body-sm tabular-nums text-text-2">
          {Math.round(zoom * 100)}%
        </span>
        <IconButton
          label="Zoom in"
          icon={<ZoomIn />}
          disabled={zoom >= ZOOM_MAX}
          onClick={() => {
            props.onZoom(zoom * ZOOM_STEP)
          }}
        />
        <IconButton
          label="Fit to width"
          icon={<Maximize2 />}
          disabled={zoom === 1}
          onClick={() => {
            props.onZoom(1)
          }}
        />
      </div>

      {(props.legend ?? true) && (
        <ul
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-text-2 lg:ml-auto"
          aria-label="Legend"
        >
          <Legend tone={OUTCOME_TONE.caught} label="Caught" />
          <Legend tone={OUTCOME_TONE.escalated} label="Escalated" />
          <Legend tone={OUTCOME_TONE.missed} label="Missed" />
          <Legend tone={FINDING_TONE} label="Our finding" dashed />
        </ul>
      )}
    </div>
  )
}

function Legend({
  tone,
  label,
  dashed = false,
}: {
  tone: string
  label: string
  dashed?: boolean
}) {
  return (
    <li className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={
          dashed
            ? "inline-block size-3.5 rounded-sm border-2 border-dashed"
            : "inline-block size-3.5 rounded-sm border-2"
        }
        // The box as it is drawn on the page: its outline, over a faint wash of the same tone.
        style={{
          borderColor: tone,
          backgroundColor: `color-mix(in srgb, ${tone} 15%, transparent)`,
        }}
      />
      {label}
    </li>
  )
}
